import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * Shared bottom-of-page CTA for dark-themed Solution pages.
 *
 * Props:
 *  - heading: string             — large headline
 *  - subtitle: string            — supporting line under headline
 *  - primary: { label, to }      — primary gold button (defaults to Volunteer)
 *  - secondary: { label, to }    — outlined secondary (defaults to Contact)
 *  - tertiary: { label, to }     — optional third outlined button
 *  - next: { label, to }         — optional "next solution" gold accent link
 *  - testId: string              — root data-testid (optional)
 */
const PolicyCTA = ({
  heading,
  subtitle,
  primary = { label: "Volunteer", to: "/volunteer" },
  secondary = { label: "Contact Us", to: "/contact" },
  tertiary,
  next,
  testId = "policy-cta",
}) => {
  return (
    <section
      data-testid={testId}
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B1A3B] to-[#0F2251]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-[#D4AF37]/[0.05] rounded-full blur-[100px]" />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
        <div className="reveal">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-4">
            {heading}
          </h2>
          {subtitle && (
            <p className="text-white/50 text-lg mb-10 max-w-xl mx-auto">
              {subtitle}
            </p>
          )}

          <div className="flex flex-wrap justify-center gap-4 mb-6">
            <Link
              to={primary.to}
              data-testid={`${testId}-primary`}
              className="group inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-12px_rgba(212,175,55,0.35)]"
            >
              {primary.label}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to={secondary.to}
              data-testid={`${testId}-secondary`}
              className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white hover:bg-white/10 font-bold uppercase tracking-widest text-sm transition-all"
            >
              {secondary.label}
            </Link>
            {tertiary && (
              <Link
                to={tertiary.to}
                data-testid={`${testId}-tertiary`}
                className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white hover:bg-white/10 font-bold uppercase tracking-widest text-sm transition-all"
              >
                {tertiary.label}
              </Link>
            )}
          </div>

          {next && (
            <Link
              to={next.to}
              data-testid={`${testId}-next`}
              className="inline-flex items-center gap-2 text-[#D4AF37] font-bold text-sm uppercase tracking-wider hover:text-[#F5D76E] transition-colors"
            >
              Next: {next.label} <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

export default PolicyCTA;
